import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sparkles, Loader2, Send, Copy, RefreshCw, Image as ImageIcon, Check, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/hooks/useFetch';
import RefImageLibrary from '@/components/admin/RefImageLibrary';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMAT_OPTIONS = [
  { value: 'standard', label: 'Standard SEO blog' },
  { value: 'listicle', label: 'Listicle (5/7/10 things…)' },
  { value: 'howto', label: 'How-to / tutorial' },
  { value: 'story', label: 'Personal story / first-person' },
  { value: 'research', label: 'Research / Journal (abstract + bibliography)' },
  { value: 'opinion', label: 'Opinion piece' },
];

const AiBlogGeneratorTab = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('standard');
  const [keywords, setKeywords] = useState('');
  const [audience, setAudience] = useState('');
  const [length, setLength] = useState('medium');
  const [referenceUrls, setReferenceUrls] = useState('');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);

  // Cover image
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverStyleHint, setCoverStyleHint] = useState('');
  // Free-form image prompt — overrides the auto-built prompt (which is
  // derived from title + excerpt + style_hint). Lets the admin describe
  // exactly what the cover should look like rather than coaxing it via
  // refinement hints. Empty → backend falls back to auto-prompt.
  const [coverCustomPrompt, setCoverCustomPrompt] = useState('');
  // Reference-library images (GridFS ids) attached to cover generation.
  const [refImageIds, setRefImageIds] = useState([]);
  // Force-preview-dark toggle — lets the admin sanity-check how the draft
  // looks against a dark background even when their system/browser is
  // in light mode. Prevents the "looked fine in admin, terrible to dark-
  // mode readers" regression that hit before the contrast sweep.
  const [previewDark, setPreviewDark] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);

  // Multi-image inline
  const [addingInline, setAddingInline] = useState(false);
  const [inlineImagesAdded, setInlineImagesAdded] = useState(0);

  const generate = async () => {
    if (prompt.trim().length < 8) return toast.error('Prompt too short');
    setGenerating(true);
    setDraft(null);
    setCoverUrl(null);
    const refs = referenceUrls
      .split(/\n|,/)
      .map((s) => s.trim())
      .filter((s) => /^https?:\/\//.test(s));
    const { data, error } = await apiFetch('/api/admin/ai-blog/generate', {
      method: 'POST',
      body: {
        prompt: prompt.trim(),
        format,
        target_keywords: keywords ? keywords.split(',').map((k) => k.trim()).filter(Boolean) : null,
        audience: audience || null,
        length,
        reference_urls: format === 'research' ? refs : null,
      },
    });
    setGenerating(false);
    if (error) return toast.error(error);
    setDraft(data);
    toast.success('Draft ready');
  };

  const generateCover = async () => {
    if (!draft) return;
    setGeneratingCover(true);
    const { data, error } = await apiFetch('/api/admin/ai-blog/cover-image', {
      method: 'POST',
      body: {
        title: draft.title,
        excerpt: draft.excerpt,
        style_hint: coverStyleHint || null,
        custom_prompt: coverCustomPrompt || null,
        reference_image_ids: refImageIds,
      },
    });
    setGeneratingCover(false);
    if (error) return toast.error(error);
    setCoverUrl(data.cover_url);
    toast.success('Cover generated');
  };

  const addInlineImages = async () => {
    if (!draft) return;
    setAddingInline(true);
    const { data, error } = await apiFetch('/api/admin/ai-blog/inline-images', {
      method: 'POST',
      body: {
        content: draft.content,
        title: draft.title,
        style_hint: coverStyleHint || null,
        mode: 'auto',
      },
    });
    setAddingInline(false);
    if (error) return toast.error(error);
    setDraft({
      ...draft,
      content: data.content_with_images,
      content_html: data.content_html || draft.content_html,
    });
    setInlineImagesAdded(data.images_added);
    toast.success(`Added ${data.images_added} inline image(s)`);
  };

  const copyContent = () => {
    if (!draft) return;
    const md = `# ${draft.title}\n\n_${draft.excerpt}_\n\n${draft.content}`;
    navigator.clipboard.writeText(md).then(
      () => toast.success('Markdown copied'),
      () => toast.error('Copy failed'),
    );
  };

  const sendToEditor = () => {
    if (!draft) return;
    sessionStorage.setItem('aiBlogDraft', JSON.stringify({
      ...draft,
      cover_image: coverUrl, // Attach approved cover
    }));
    toast.success('Draft sent to editor');
    navigate('/blog/admin?from=ai-draft');
  };

  return (
    <div className="space-y-4" data-testid="ai-blog-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#00A896]" /> AI Blog Generator
          </CardTitle>
          <CardDescription>
            Claude Sonnet 4.5 drafts. Pick a format, generate, review, optionally add an AI cover image, then send to the editor for scheduling/publishing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="ai-prompt">Topic / prompt</Label>
            <Textarea
              id="ai-prompt"
              rows={3}
              placeholder='e.g. "Why neurotypical people say lets circle back — what they actually mean"'
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="ai-prompt-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="ai-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMAT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Length</Label>
              <Select value={length} onValueChange={setLength}>
                <SelectTrigger data-testid="ai-length-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (600-800 words)</SelectItem>
                  <SelectItem value="medium">Medium (1000-1200 words)</SelectItem>
                  <SelectItem value="long">Long (1400-1800 words)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ai-keywords">Target keywords (comma-separated)</Label>
              <Input
                id="ai-keywords"
                placeholder="workplace communication, ADHD"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                data-testid="ai-keywords-input"
              />
            </div>
            <div>
              <Label htmlFor="ai-audience">Audience</Label>
              <Input
                id="ai-audience"
                placeholder="ADHD adults in tech jobs"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                data-testid="ai-audience-input"
              />
            </div>
          </div>

          {format === 'research' && (
            <div className="border-l-4 border-[#00A896] pl-3 py-2 bg-[#00E5CC]/5 rounded">
              <Label htmlFor="ai-refs">Reference URLs (one per line — used as bibliography)</Label>
              <Textarea
                id="ai-refs"
                rows={4}
                placeholder={'https://psycnet.apa.org/...\nhttps://www.additudemag.com/...'}
                value={referenceUrls}
                onChange={(e) => setReferenceUrls(e.target.value)}
                data-testid="ai-refs-input"
                className="mt-1 font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Model will draw on these as sources and include a "Works Cited" section at the end.
              </p>
            </div>
          )}

          <Button
            onClick={generate}
            disabled={generating || prompt.trim().length < 8}
            className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
            data-testid="ai-generate-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? 'Generating (15-30s)...' : 'Generate draft'}
          </Button>
        </CardContent>
      </Card>

      {draft && (
        <Card data-testid="ai-draft-card">
          <CardHeader>
            <CardTitle className="text-base">{draft.title}</CardTitle>
            <CardDescription>{draft.meta_description}</CardDescription>
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge className="bg-[#00E5CC]/10 text-[#0d1f2d] border-[#00E5CC]/30">
                {FORMAT_OPTIONS.find((f) => f.value === draft.format)?.label || draft.format}
              </Badge>
              <Badge variant="outline">{draft.category}</Badge>
              {(draft.tags || []).map((t) => (<Badge key={t} variant="secondary">{t}</Badge>))}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setPreviewDark((v) => !v)}
                title="Sanity-check how this draft looks against a dark background before publishing"
                data-testid="ai-preview-theme-toggle"
              >
                {previewDark
                  ? <><Sun className="h-3 w-3 mr-1" /> Preview on light</>
                  : <><Moon className="h-3 w-3 mr-1" /> Preview on dark</>}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={
                previewDark
                  ? 'border rounded p-4 bg-slate-900 border-slate-700 max-h-[400px] overflow-auto'
                  : 'border rounded p-4 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 max-h-[400px] overflow-auto'
              }
              data-testid="ai-draft-preview-wrapper"
            >
              <pre
                className={
                  previewDark
                    ? 'whitespace-pre-wrap text-sm font-sans text-slate-100'
                    : 'whitespace-pre-wrap text-sm font-sans text-foreground'
                }
                data-testid="ai-draft-content"
              >
                {draft.content}
              </pre>
            </div>

            {/* Cover image approval workflow */}
            <div className="border rounded p-4 bg-white dark:bg-slate-800/50 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" /> Cover image (optional, for approval)
                </Label>
                {coverUrl && (
                  <Badge className="bg-emerald-100 text-emerald-800">
                    <Check className="h-3 w-3 mr-1" /> Approved & ready
                  </Badge>
                )}
              </div>
              {coverUrl ? (
                <div className="flex flex-col md:flex-row gap-3 items-start">
                  <img
                    src={`${API}${coverUrl}?v=${Date.now()}`}
                    alt="cover preview"
                    className="w-40 h-40 object-cover rounded border"
                    data-testid="ai-cover-preview"
                  />
                  <div className="flex-1 space-y-2">
                    <div>
                      <Label htmlFor="cover-custom-prompt" className="text-xs">
                        Image prompt <span className="text-muted-foreground font-normal">(overrides auto-prompt — describe exactly what you want)</span>
                      </Label>
                      <Textarea
                        id="cover-custom-prompt"
                        rows={2}
                        placeholder='e.g. "A friendly cartoon dinosaur and a person at a coffee shop table, soft morning light, illustrated flat style"'
                        value={coverCustomPrompt}
                        onChange={(e) => setCoverCustomPrompt(e.target.value)}
                        data-testid="ai-cover-custom-prompt-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cover-hint" className="text-xs">
                        Style hint <span className="text-muted-foreground font-normal">(only used when Image prompt is empty)</span>
                      </Label>
                      <Textarea
                        id="cover-hint"
                        rows={2}
                        placeholder='e.g. "More dramatic lighting, darker palette, no dinosaur"'
                        value={coverStyleHint}
                        onChange={(e) => setCoverStyleHint(e.target.value)}
                        data-testid="ai-cover-hint-input"
                      />
                    </div>
                    <RefImageLibrary selected={refImageIds} onChange={setRefImageIds} />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={generateCover}
                        disabled={generatingCover}
                        data-testid="ai-cover-regen"
                      >
                        {generatingCover ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                        Regenerate
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCoverUrl(null)}
                        data-testid="ai-cover-clear"
                      >
                        Remove cover
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="cover-custom-prompt-pre" className="text-xs">
                    Image prompt <span className="text-muted-foreground font-normal">(optional — overrides the auto-prompt built from title + excerpt)</span>
                  </Label>
                  <Textarea
                    id="cover-custom-prompt-pre"
                    rows={2}
                    placeholder='e.g. "A friendly cartoon dinosaur and a person at a coffee shop table, soft morning light, illustrated flat style"'
                    value={coverCustomPrompt}
                    onChange={(e) => setCoverCustomPrompt(e.target.value)}
                    data-testid="ai-cover-custom-prompt-input-pre"
                  />
                  <RefImageLibrary selected={refImageIds} onChange={setRefImageIds} />
                  <Button
                    variant="outline"
                    onClick={generateCover}
                    disabled={generatingCover}
                    data-testid="ai-cover-generate"
                  >
                    {generatingCover ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                    Generate cover with AI
                  </Button>
                </div>
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={addInlineImages}
                disabled={addingInline}
                data-testid="ai-add-inline-images"
              >
                {addingInline ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                {inlineImagesAdded > 0 ? `Regenerate inline images (${inlineImagesAdded} added)` : 'Add inline images (one per H2)'}
              </Button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={sendToEditor}
                className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                data-testid="ai-send-to-editor"
              >
                <Send className="h-4 w-4 mr-2" /> Send to Blog Editor
              </Button>
              <Button variant="outline" onClick={copyContent} data-testid="ai-copy-markdown">
                <Copy className="h-4 w-4 mr-2" /> Copy markdown
              </Button>
              <Button variant="ghost" onClick={() => { setDraft(null); setCoverUrl(null); setInlineImagesAdded(0); }} data-testid="ai-discard">
                Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AiBlogGeneratorTab;
